import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fetcher } from '@/lib/api'

/**
 * Render a subscription expiry for display.
 *
 * Deliberately NOT convertUTCToKL / formatKLDate. `expires_at` is the end of a
 * DATE (the server turns `expiry_date` into 23:59:59 of that day), so treating
 * it as an instant and shifting it into Kuala Lumpur rolls it onto the next
 * day — telling a learner their subscription ended on the 2nd when it ended on
 * the 1st. The date part is the answer; there is no timezone in it to correct.
 */
export const formatSubscriptionExpiry = (value: string | null): string | null => {
  if (!value) return null

  const [datePart] = value.split(' ')
  const parsed = new Date(`${datePart}T00:00:00`)

  return Number.isNaN(parsed.getTime()) ? null : format(parsed, 'dd MMM yyyy')
}

/**
 * `none` means the learner has no package subscription at all, which is NOT
 * the same as expired — most workspaces never sold one, and their learners
 * must keep full access. Only `expired` restricts anything.
 */
export type SubscriptionState = 'none' | 'active' | 'expired'

export interface SubscriptionStatus {
  state: SubscriptionState
  expires_at: string | null
}

interface SubscriptionResponse {
  success: boolean
  data?: SubscriptionStatus
}

/**
 * The learner's current subscription standing.
 *
 * Read from the API rather than the user store because the store is hydrated
 * at login and never refreshed: a subscription that lapses mid-session would
 * go unnoticed, and the learner would meet a 403 with no warning. Cached for
 * five minutes — an expiry date moves rarely, and this runs on every portal
 * page.
 *
 * /me/subscription rather than /auth/me: that route is not workspace-scoped,
 * so it cannot resolve which workspace the learner is in, and a subscription
 * means nothing without one.
 */
export const useSubscriptionStatus = (enabled = true) => {
  const query = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => fetcher<SubscriptionResponse>('/me/subscription'),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  })

  // Absent data (request failed, or a super admin with no workspace) reads as
  // "nothing to restrict" rather than locking the UI.
  const status = query.data?.data ?? { state: 'none' as const, expires_at: null }

  // Has the question been answered yet? Until it has, `isExpired` is false
  // simply because nothing is known — and a guard that acts on that lets the
  // very thing it is meant to stop happen on the first render. Pages that
  // block an action must wait for this; the banner does not need to, because
  // showing nothing while loading is already correct.
  const statusKnown = query.isSuccess || query.isError

  return {
    ...query,
    status,
    statusKnown,
    isExpired: status.state === 'expired',
    expiresAt: status.expires_at,
  }
}
